import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex } from 'antd';

import { roundToDecimals } from '@/shared/lib/math';
import { AlgotechIndex } from '@/shared/ui/algotech-index';
import { PatternStatsTable } from '@/shared/ui/patterns';
import { DirectionTypeValue } from '@/shared/ui/value-formatter';

import type { PatternStudy } from '../../model/types/pattern-study';

import cls from './pattern-lab-info.module.css';

type PatternStudyInfoProps = {
    patterns: PatternStudy[];
};

export const PatternLabInfo = memo(({ patterns }: PatternStudyInfoProps) => {
    const { t } = useTranslation('pattern-studio');

    return (
        <Flex
            data-tour="pattern-lab-info"
            gap="16px"
            style={{ width: '100%', height: '100%' }}
        >
            {patterns.map((patternStudy) => (
                <div
                    key={patternStudy.pattern.unique_id}
                    className={cls.item}
                >
                    <PatternStatsTable
                        accentColor={patternStudy.color}
                        data={[
                            /* eslint-disable no-underscore-dangle */
                            {
                                label: t('patternStats.ticker'),
                                value: patternStudy.pattern.Ticker,
                                helpId: 'PATTERN_INFO_TICKER',
                            },
                            {
                                label: t('patternStats.timeframe'),
                                value: patternStudy.pattern.Timeframe,
                                helpId: 'PATTERN_INFO_TIMEFRAME',
                            },
                            {
                                label: t('patternStats.length'),
                                value: patternStudy.pattern.pattern_len_,
                                helpId: 'PATTERN_INFO_LENGTH',
                            },
                            {
                                label: t('patternStats.dd'),
                                value: `${roundToDecimals(
                                    patternStudy.pattern
                                        .price_drawdown_post_mean,
                                )}%`,
                                helpId: 'PATTERN_INFO_PRICE_DRAWDOWN_POST_MEAN',
                            },
                            {
                                label: t('patternStats.type'),
                                value: (
                                    <DirectionTypeValue
                                        isRecovered={
                                            !!patternStudy.pattern
                                                .direction_type
                                        }
                                    />
                                ),
                                helpId: 'PATTERN_INFO_DIRECTION_TYPE',
                            },
                            {
                                label: t('patternStats.avgProfit'),
                                value: `${roundToDecimals(
                                    patternStudy.pattern
                                        .price_max_variation_post_mean,
                                )}%`,
                                helpId: 'PATTERN_INFO_PRICE_MAX_VARIATION_POST_MEAN',
                            },
                            {
                                label: t('patternStats.threshold'),
                                value: `${roundToDecimals(
                                    patternStudy.pattern
                                        .price_threshold_post_mean,
                                )}%`,
                                helpId: 'PATTERN_INFO_PRICE_THRESHOLD_POST_MEAN',
                            },
                            {
                                label: t('patternStats.algotechIndex'),
                                value: (
                                    <AlgotechIndex
                                        value={
                                            patternStudy.pattern.algotech_index
                                        }
                                    />
                                ),
                                helpId: 'PATTERN_INFO_ALGOTECH_INDEX',
                            },
                            /* eslint-enable no-underscore-dangle */
                        ]}
                        helpId="PATTERN_INFO"
                        isFollowingCursor
                        title={t('patternStats.patternInfo')}
                    />
                </div>
            ))}
        </Flex>
    );
});

PatternLabInfo.displayName = 'PatternStudyInfo';
